import React, { useState } from "react"
import Layout from "../components/layout"
import Head from "../components/head"
import Spinner from "../components/spinner"
import aboutMeStyles from "./about-me.module.scss"
import commonStyles from "./common-styles.module.scss"

const AboutMePage = () => {
  const [vidLoaded, setVidLoaded] = useState(false)
  return (
    <Layout isHome={false}>
      <Head
        title="About Me"
        description="An integrative therapist and coach, working holistically and bringing awareness of privilege and oppression to the process."
      />
      <h2>About Me</h2>
      <h3 className={aboutMeStyles.subtitle}>What Guides My Work</h3>
      <p>
        A central driver in my life is a desire to help people live in more
        harmony with ourselves, others, and the world. I believe we can create a
        more just and caring society where people feel more unconditionally
        taken care of. I am passionate about supporting people in their journeys
        towards greater awareness, vitality, and alignment with their values.
      </p>
      <blockquote className={commonStyles.quoteBlock}>
        <p>
          <q>
            <cite>
              Wanna fly, you got to give up the s**t that weighs you down.
            </cite>
          </q>{" "}
          <span className={commonStyles.quoteAuthor}>Toni Morrison</span>
        </p>
      </blockquote>
      <h3 className={aboutMeStyles.subtitle}>My Background and Influences</h3>
      <p>
        In my work as a therapist and coach, I integrate a range of therapeutic
        models I have studied, primarily{" "}
        <strong>Cognitive Behavioural Therapy (CBT)</strong> and
        <strong> Internal Family Systems (IFS)</strong>.
      </p>
      <p>
        My practice is also informed by my experience in:
        <ul>
          <li>Activism for social and environmental justice</li>
          <li>Group facilitation</li>
          <li>Participation in feminist orientated men's circles</li>
          <li>
            and my lived experience as a partner, son, parent, and migrant
          </li>
        </ul>
      </p>
      <p>
        I believe the power of talking therapies is supported by more embodied
        experiences <span>&#8212;</span> for example, meditation and dance{" "}
        <span>&#8212;</span> which can also be important tools in my work.
      </p>
      <h3 className={aboutMeStyles.subtitle}>
        Being a Therapist Who Also Does the Work
      </h3>
      <p>
        I have benefited, and continue to benefit, from working with some
        wonderful therapists myself—both individually and as part of a couple.
      </p>
      <p>
        I believe it is important for therapists to work actively on ourselves,
        and to keep a close connection with what it is like to be a client in
        therapy.
      </p>
      <h3 className={aboutMeStyles.subtitle}>
        How I See the Therapeutic Relationship
      </h3>
      <p>
        I enjoy the challenge of working compassionately with every part of
        every person I work with. This includes maintaining boundaries and an
        awareness of privileges and oppressions, so that therapy can remain a{" "}
        safe and respectful space for everyone.
      </p>
      <blockquote
        className={`${commonStyles.quoteBlock} ${aboutMeStyles.quoteAboveVideo}`}
      >
        <p>
          <q>
            <cite>
              Fight against the strong when we are weak and against ourselves
              when we are strong.
            </cite>
          </q>{" "}
          <span className={commonStyles.quoteAuthor}>Lluís Maria Xirinacs</span>
        </p>
      </blockquote>
      <div className={aboutMeStyles.videoContainer}>
        {!vidLoaded && <Spinner />}
        <iframe
          className={aboutMeStyles.video}
          title="Gavin Lashley Therapy - An Introduction"
          src="https://player.vimeo.com/video/485165963"
          allow="autoplay; fullscreen"
          onLoad={() => setVidLoaded(true)}
        ></iframe>
      </div>
      <h3 className={aboutMeStyles.subtitle}>My Education/Qualifications</h3>
      <ul>
        <li>
          Diploma in Counselling and Groupwork - A Cognitive Behavioural
          Approach{" "}
          <span className={aboutMeStyles.parenthetic}>
            (Centre of Therapy, <i>2017</i>)
          </span>
        </li>
        <li>
          IFS Level 1 & 2{" "}
          <span className={aboutMeStyles.parenthetic}>
            (IFS Institute<i>, 2023 & 2025</i>)
          </span>
        </li>
        <li>
          Intimacy From The Inside Out Couples Therapy{" "}
          <span className={aboutMeStyles.parenthetic}>
            (IFS Institute<i>, 2025</i>)
          </span>
        </li>
        <li>
          Dance Movement Therapy{" "}
          <span className={aboutMeStyles.parenthetic}>
            (Danza Interior<i>, 2023</i>)
          </span>
        </li>
        <li>
          Psychology MA 2:1 degree{" "}
          <span className={aboutMeStyles.parenthetic}>
            (Edinburgh University,
            <i> 2007</i>)
          </span>
        </li>
        <li>
          8-week Mindfulness Based Stress Reduction (MBSR) course{" "}
          <span className={aboutMeStyles.parenthetic}>
            (<i>2016</i>)
          </span>
        </li>
        <li>
          Rethinking Masculinities as a gender violence reduction strategy{" "}
          <span className={aboutMeStyles.parenthetic}>
            (<i>2015</i>)
          </span>
        </li>
      </ul>
      <h3
        className={`${aboutMeStyles.subtitle} ${aboutMeStyles.membershipsHeading}`}
      >
        Memberships:
      </h3>
      <ul>
        <li>
          <a
            href="https://www.bacp.co.uk/"
            target="_blank"
            rel="noreferrer noopener"
          >
            British Association for Counselling and Psychotherapy (BACP)
          </a>
        </li>
        <li>AXA Civil Liability Insurance</li>
      </ul>
    </Layout>
  )
}

export default AboutMePage
